/**
 * Configuration page — the single live customer profile (F6.x).
 *
 * The config is one mutable record per deployment (no time-bounded versions).
 * Any compliance role may read it; a admin can edit a field in place:
 * click the pencil, the value becomes a text box (or select), enter an audit
 * reason, press OK — the field is PATCHed onto the live config. The live config
 * is resolved org-scoped from the session (GET /config/active, no profile_key),
 * so the app stays customer-agnostic even though each deployment is single-tenant
 * and works before any batch exists.
 *
 * Editing a *constant* (EMTS, substitution, timezone, …) recomputes all batches.
 * The "per-batch defaults" block (conversion factors, calc methods, Token/RIN
 * MCV) only seeds the pre-fill for NEW batches — existing batches keep the values
 * frozen on them at creation.
 */

import { Decimal, isApiError } from "@compliance/domain";
import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  Group,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconCheck, IconPencil, IconSettings, IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useActiveConfig, useUpdateConfig } from "../api/hooks";
import type { CalcMethods, ConfigVersion, ConversionFactors, CustomerProfile } from "../api/types";
import { useHasRole } from "../auth";
import { queryKeys } from "../lib/queryKeys";

export function ConfigPage() {
  // The live config resolves org-scoped from the session — no profile key or
  // batch needed. A 404 means the deployment simply hasn't been seeded yet,
  // which we surface distinctly from an actual load failure.
  const activeQ = useActiveConfig();
  const notSeeded =
    activeQ.isError && isApiError(activeQ.error) && activeQ.error.httpStatus === 404;

  return (
    <Stack gap="lg">
      <Group gap="sm">
        <ThemeIcon variant="light" radius="md" size="lg">
          <IconSettings size={20} />
        </ThemeIcon>
        <Stack gap={0}>
          <Title order={2}>Configuration</Title>
          <Text size="sm" c="dimmed">
            The live customer profile — EMTS identifiers, conversion factors, thresholds, pathways.
          </Text>
        </Stack>
      </Group>

      {activeQ.isPending ? (
        <Skeleton height={360} radius="md" />
      ) : notSeeded ? (
        <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">
            No configuration has been seeded for this deployment yet. It appears here once the
            customer profile is provisioned.
          </Text>
        </Alert>
      ) : activeQ.isError || !activeQ.data ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">Couldn't resolve the active configuration profile.</Text>
        </Alert>
      ) : (
        <ConfigEditor config={activeQ.data} />
      )}
    </Stack>
  );
}

function ConfigEditor({ config }: { config: ConfigVersion }) {
  const canEdit = useHasRole("admin");
  const update = useUpdateConfig();
  const queryClient = useQueryClient();

  const content = config.content;
  const profileKey = config.profile_key;

  // Apply `mutate` to the live content and PATCH the whole record. Spreading the
  // parsed `content` preserves backend-only keys (lcfs, data_quality, …) the FE
  // doesn't surface, so an edit never resets them.
  const saveField = async (
    mutate: (c: CustomerProfile) => CustomerProfile,
    reason: string,
  ): Promise<void> => {
    const next = mutate(content);
    await update.mutateAsync({
      input: { profile_key: profileKey, content: next },
      reason,
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.config.all });
    notifications.show({
      title: "Configuration updated",
      message: "The live configuration was saved.",
      color: "teal",
      icon: <IconCheck size={18} />,
    });
  };

  const cf =
    (key: keyof ConversionFactors) =>
    (value: string, reason: string): Promise<void> =>
      saveField(
        (c) => ({ ...c, conversion_factors: { ...c.conversion_factors, [key]: value } }),
        reason,
      );

  const cm =
    (key: keyof CalcMethods) =>
    (value: string, reason: string): Promise<void> =>
      saveField(
        (c) => ({ ...c, calc_methods: { ...c.calc_methods, [key]: value } as CalcMethods }),
        reason,
      );

  const emts =
    (key: keyof CustomerProfile["emts"]) =>
    (value: string, reason: string): Promise<void> =>
      saveField((c) => ({ ...c, emts: { ...c.emts, [key]: value } }), reason);

  const sub =
    (key: "max_fraction" | "max_consecutive_days") =>
    (value: string, reason: string): Promise<void> =>
      saveField(
        (c) => ({
          ...c,
          substitution: {
            ...c.substitution,
            [key]: key === "max_consecutive_days" ? Number(value) : value,
          },
        }),
        reason,
      );

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Text size="sm" c="dimmed">
          Profile
        </Text>
        <Badge variant="light" size="lg">
          {profileKey}
        </Badge>
        {!canEdit ? (
          <Text size="xs" c="dimmed">
            (read-only — editing requires the admin role)
          </Text>
        ) : null}
      </Group>

      <Card withBorder radius="md" padding="lg">
        <Stack gap="lg">
          <Section
            tour="config-batch-defaults"
            title="Per-batch defaults"
            hint="Pre-fills a new batch; existing batches keep the values frozen on them."
          >
            <EditableField
              label="Token MCV (MMBtu)"
              value={content.token_mcv_mmbtu}
              mono
              numeric2dp
              canEdit={canEdit}
              onSave={(v, r) => saveField((c) => ({ ...c, token_mcv_mmbtu: v }), r)}
            />
            <EditableField
              label="RIN MCV (MMBtu)"
              value={content.rin_mcv_mmbtu}
              mono
              numeric2dp
              canEdit={canEdit}
              onSave={(v, r) => saveField((c) => ({ ...c, rin_mcv_mmbtu: v }), r)}
            />
            {/* Full precision: the two ratios differ in the 5th decimal
                (0.900414190526 vs 0.9004), which a 2 dp view would hide. */}
            <EditableField
              label="LHV/HHV ratio (RFS4800)"
              value={content.conversion_factors.lhv_hhv_ratio}
              mono
              canEdit={canEdit}
              onSave={cf("lhv_hhv_ratio")}
            />
            <EditableField
              label="LHV/HHV ratio (RF Gallons)"
              value={
                content.conversion_factors.rin_lhv_hhv_ratio ??
                content.conversion_factors.lhv_hhv_ratio
              }
              mono
              canEdit={canEdit}
              onSave={cf("rin_lhv_hhv_ratio")}
            />
            <EditableField
              label="BTU LHV per RIN"
              value={content.conversion_factors.btu_lhv_per_rin}
              mono
              numeric2dp
              canEdit={canEdit}
              onSave={cf("btu_lhv_per_rin")}
            />
            <EditableField
              label="MMBtu per GJ"
              value={content.conversion_factors.mmbtu_per_gj}
              mono
              numeric2dp
              canEdit={canEdit}
              onSave={cf("mmbtu_per_gj")}
            />
            <EditableField
              label="BTU per scf (HHV)"
              value={content.conversion_factors.btu_per_scf_hhv}
              mono
              numeric2dp
              canEdit={canEdit}
              onSave={cf("btu_per_scf_hhv")}
            />
            <EditableSelect
              label="Token MMBtu method"
              value={content.calc_methods.token_mmbtu}
              data={[
                { value: "energy_totalizer", label: "energy_totalizer" },
                { value: "scf_ch4_btu", label: "scf_ch4_btu" },
              ]}
              canEdit={canEdit}
              onSave={cm("token_mmbtu")}
            />
            <EditableSelect
              label="Source granularity"
              value={content.calc_methods.source_granularity}
              data={[
                { value: "daily", label: "daily" },
                { value: "hourly_summed", label: "hourly_summed" },
              ]}
              canEdit={canEdit}
              onSave={cm("source_granularity")}
            />
          </Section>

          <Section
            tour="config-emts"
            title="EMTS identifiers"
            hint="Constant — editing recomputes all batches that read it live."
          >
            <EditableField
              label="Company name"
              value={content.emts.company_name}
              canEdit={canEdit}
              onSave={emts("company_name")}
            />
            <EditableField
              label="CID"
              value={content.emts.cid}
              mono
              canEdit={canEdit}
              onSave={emts("cid")}
            />
            <EditableField
              label="Facility name"
              value={content.emts.facility_name}
              canEdit={canEdit}
              onSave={emts("facility_name")}
            />
            <EditableField
              label="FID"
              value={content.emts.fid}
              mono
              canEdit={canEdit}
              onSave={emts("fid")}
            />
            <EditableField
              label="Facility type"
              value={content.emts.facility_type}
              canEdit={canEdit}
              onSave={emts("facility_type")}
            />
            <EditableField
              label="Fuel code"
              value={content.emts.fuel_code}
              mono
              canEdit={canEdit}
              onSave={emts("fuel_code")}
            />
            <EditableField
              label="Feedstock code"
              value={content.emts.feedstock_code}
              mono
              canEdit={canEdit}
              onSave={emts("feedstock_code")}
            />
          </Section>

          <Section
            title="Substitution thresholds (TGP)"
            hint="Constant — recomputes all batches."
            tour="config-substitution"
          >
            <EditableField
              label="Max fraction"
              value={content.substitution.max_fraction}
              mono
              numeric2dp
              canEdit={canEdit}
              onSave={sub("max_fraction")}
            />
            <EditableField
              label="Max consecutive days"
              value={String(content.substitution.max_consecutive_days)}
              mono
              canEdit={canEdit}
              onSave={sub("max_consecutive_days")}
            />
          </Section>

          <Section title={`Reporting entities (${content.reporting_entities.length})`}>
            <MiniTable
              head={["Role", "Name", "Identifier"]}
              rows={content.reporting_entities.map((e) => [e.role, e.name, e.identifier])}
            />
          </Section>

          <Section
            title={`Fuel pathway codes (${content.fuel_pathway_codes.length})`}
            tour="config-pathways"
          >
            <MiniTable
              head={["FPC", "Dairy", "Status", <CiHeader key="ci" />]}
              rows={content.fuel_pathway_codes.map((f) => [
                f.fpc_code,
                `${f.dairy_name} (${f.dairy_identifier})`,
                f.status,
                f.certified_ci_gco2e_per_mj ? round2(f.certified_ci_gco2e_per_mj) : "—",
              ])}
            />
          </Section>
        </Stack>
      </Card>
    </Stack>
  );
}

// ── inline editors ──────────────────────────────────────────────────────────

/** Audit reason is mandatory on every config mutation; min 3 chars. */
function reasonOk(reason: string): boolean {
  return reason.trim().length >= 3;
}

/**
 * Trim a decimal-string to at most 2 decimal places for display/editing.
 * Integers and ≤2-dp values are returned unchanged (no forced trailing zeros,
 * no thousands separators); non-numeric strings fall through untouched. The
 * stored config keeps its full precision until an admin saves an edit.
 */
function round2(value: string): string {
  try {
    return new Decimal(value).toDecimalPlaces(2).toString();
  } catch {
    return value;
  }
}

function EditableField({
  label,
  value,
  mono,
  numeric2dp,
  canEdit,
  onSave,
}: {
  label: string;
  value: string;
  mono?: boolean;
  /** Display/edit the value at ≤2 decimal places (numeric measurement fields). */
  numeric2dp?: boolean;
  canEdit: boolean;
  onSave: (value: string, reason: string) => Promise<void>;
}) {
  // What the user sees and edits. For numeric fields this is the stored value
  // trimmed to 2 dp, so the read view, the edit box, and the no-change check
  // all agree — opening + OK-ing an untouched field never rounds the stored value.
  const shown = numeric2dp ? round2(value) : value;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shown);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const begin = () => {
    setDraft(shown);
    setReason("");
    setError(null);
    setEditing(true);
  };
  const cancel = () => {
    if (busy) return;
    setEditing(false);
  };
  const confirm = async () => {
    if (!reasonOk(reason) || draft === shown) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.trim(), reason.trim());
      setEditing(false);
    } catch (e) {
      setError(
        isApiError(e) ? `${e.message} (${e.code})` : ((e as Error)?.message ?? "Save failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <Group gap="sm" wrap="nowrap" align="center">
        <Text size="sm" c="dimmed" w={180} style={{ flexShrink: 0 }}>
          {label}
        </Text>
        <Text size="sm" ff={mono ? "monospace" : undefined}>
          {shown}
        </Text>
        {canEdit ? (
          <Tooltip label={`Edit ${label}`} withinPortal>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Edit ${label}`}
              onClick={begin}
            >
              <IconPencil size={15} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>
    );
  }

  return (
    <Paper withBorder radius="sm" p="sm" bg="var(--mantine-color-indigo-0)">
      <Stack gap="xs">
        <Group gap="sm" wrap="nowrap" align="flex-end">
          <TextInput
            label={label}
            size="xs"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            styles={mono ? { input: { fontFamily: "monospace" } } : undefined}
            style={{ flex: 1 }}
            data-autofocus
          />
          <ActionIcon
            variant="filled"
            color="teal"
            aria-label="Save"
            disabled={!reasonOk(reason) || draft === shown || busy}
            loading={busy}
            onClick={confirm}
          >
            <IconCheck size={16} />
          </ActionIcon>
          <ActionIcon variant="default" aria-label="Cancel" disabled={busy} onClick={cancel}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <TextInput
          label="Reason"
          description="Recorded in the audit log. Minimum 3 characters."
          size="xs"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonOk(reason) ? "Minimum 3 characters" : null}
        />
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

function EditableSelect({
  label,
  value,
  data,
  canEdit,
  onSave,
}: {
  label: string;
  value: string;
  data: { value: string; label: string }[];
  canEdit: boolean;
  onSave: (value: string, reason: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const begin = () => {
    setDraft(value);
    setReason("");
    setError(null);
    setEditing(true);
  };
  const confirm = async () => {
    if (!reasonOk(reason) || draft === value) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(draft, reason.trim());
      setEditing(false);
    } catch (e) {
      setError(
        isApiError(e) ? `${e.message} (${e.code})` : ((e as Error)?.message ?? "Save failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <Group gap="sm" wrap="nowrap" align="center">
        <Text size="sm" c="dimmed" w={180} style={{ flexShrink: 0 }}>
          {label}
        </Text>
        <Text size="sm" ff="monospace">
          {value}
        </Text>
        {canEdit ? (
          <Tooltip label={`Edit ${label}`} withinPortal>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Edit ${label}`}
              onClick={begin}
            >
              <IconPencil size={15} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>
    );
  }

  return (
    <Paper withBorder radius="sm" p="sm" bg="var(--mantine-color-indigo-0)">
      <Stack gap="xs">
        <Group gap="sm" wrap="nowrap" align="flex-end">
          <Select
            label={label}
            size="xs"
            data={data}
            value={draft}
            onChange={(v) => setDraft(v ?? draft)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="filled"
            color="teal"
            aria-label="Save"
            disabled={!reasonOk(reason) || draft === value || busy}
            loading={busy}
            onClick={confirm}
          >
            <IconCheck size={16} />
          </ActionIcon>
          <ActionIcon
            variant="default"
            aria-label="Cancel"
            disabled={busy}
            onClick={() => setEditing(false)}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <TextInput
          label="Reason"
          description="Recorded in the audit log. Minimum 3 characters."
          size="xs"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonOk(reason) ? "Minimum 3 characters" : null}
        />
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

// ── small presentational helpers ────────────────────────────────────────────

function Section({
  title,
  hint,
  tour,
  children,
}: {
  title: string;
  hint?: string;
  /** `data-tour` anchor for the guided demo tour. */
  tour?: string;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={6} data-tour={tour}>
      <Text size="sm" fw={600} tt="uppercase" lts="0.04em" c="dimmed">
        {title}
      </Text>
      {hint ? (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      ) : null}
      {children}
    </Stack>
  );
}

/**
 * The pathway CI column's header, which needs a caveat the label cannot hold.
 *
 * Every row under it reads the same interim figure, because the config's
 * pathway roster is projected from the LCFS reference tables and the projection
 * deliberately emits the value CARB applies to a pending pathway — the one the
 * already-filed artifacts were computed with. The LCFS tab's dairy registry
 * shows the certified intensities off the same tables, so an unqualified "CI"
 * here reads as a contradiction of that screen.
 */
function CiHeader() {
  return (
    <Tooltip
      multiline
      w={280}
      withArrow
      label="The interim value CARB applies to a pending pathway — the figure the filed artifacts were computed with. Certified carbon intensities are on the LCFS tab's dairy registry."
    >
      <Text component="span" size="sm" fw={700} style={{ cursor: "help" }}>
        CI (gCO2e/MJ) — interim
      </Text>
    </Tooltip>
  );
}

/**
 * `head` takes nodes, not just strings, so a column whose meaning needs a
 * caveat can carry it in a tooltip instead of in the label. Keys are positional
 * because a node cannot be one; the header of a fixed table never reorders.
 */
function MiniTable({ head, rows }: { head: React.ReactNode[]; rows: string[][] }) {
  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        None configured.
      </Text>
    );
  }
  return (
    <Paper withBorder radius="sm">
      <ScrollArea>
        <Table fz="sm" verticalSpacing="xs" horizontalSpacing="md">
          <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
            <Table.Tr>
              {head.map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a node is not a key
                <Table.Th key={i}>{h}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.join("|")}>
                {r.map((cell, i) => (
                  <Table.Td key={`${head[i]}:${cell}`} fz="xs">
                    {cell}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}
